import { useContext, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation, generatePath } from 'react-router-dom';
import { useDrop } from 'react-dnd';
import { useIntl } from 'react-intl';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import type { TabProps } from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import { styled, alpha, type Theme } from '@mui/material/styles';
import { pipe, Array as A } from 'effect';
import cx from 'classnames';

import { documentCategorizationAllowedSelector, documentsChangeTypesAction } from '@/domain/documents';
import { getNestedCategory, findCatById } from '@/domain/categories/helpers';
import { doctypeTabLookupSelector, categoriesListSelector } from '@/domain/categories/selectors';
import { DOCUMENT } from '@/components/DnDItem';
import RouterLink from '@/components/mui/Router/RouterLink';
import CONST from '@/domain/documents/constants';
import toast from '@/components/Toast';
import CompanyWorkspaceContext from '@/pages/company/WorkspaceRoute/CompanyWorkspaceContext';
import { useWorkspaceCategorization } from '@/pages/company/WorkspaceRoute/hooks/useWorkspaceAggregations';
import { useCanReclassify } from '@/pages/company/WorkspaceRoute/hooks/useCanReclassify';
import CategorizationSkeletons from '@/pages/company/WorkspaceRoute/components/CategorizationSkeletons';
import ROUTES_PATH from '@/router/routesPathConfig';

// Routed doctype tab bar (spec da-19295 D7). Each tab is a REAL link (repo pattern: BankFeedRoute,
// @/components/Tabs), never an imperative onChange: the URL shows on hover, cmd/middle-click opens a
// new tab, and the global query rides `location.search` by construction (DA-19705). MUI resolves its
// anchor overload on `href`, not react-router's `to`, hence `component={RouterLink} href={…}`.
// `RouterLink` (plain Link), NOT `RouterNavLink`: selection is owned by `Tabs value=`, and NavLink's
// prefix match would put `aria-current="page"` on the All tab as well on every Fin/Gen URL.
// Active tab = deepest URL category segment matched
// against each doctype's routing `id`; on the bare category that segment equals the All tab's `id`,
// so All is active by default (appends no segment). Fin/Gen append their `id` as the
// next-deeper segment. The Financial/General tabs are also reclassify drop targets (task 4.1):
// dropping a document — or the current multi-selection — writes _T_FIN / _T_GEN. All is not a target.

// The bar's box, shared by the real tab strip and its loading skeleton. ONE source deliberately: if
// the skeleton's metrics drift from the real bar's, the sticky subheader starts jumping on load
// again — exactly what DA-19731 fixes.
// Spacing lives here, not as a bottom margin on CompanyBarPanel: this component renders null for
// non-brain companies, so a parent-side margin would leave a dead gap. WorkspaceSubtypeChips
// carries its own top padding, so the chips-without-tabs case is already covered.
const barMetrics = (theme: Theme) => ({
  minHeight: 40,
  marginTop: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
});

const StyledTabs = styled(Tabs, { label: 'WorkspaceDoctypeTabs' })(({ theme }) => ({
  ...barMetrics(theme),
  '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main },
}));

const StyledTab = styled(Tab, { label: 'WorkspaceDoctypeTab' })(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&.Mui-selected': { color: theme.palette.primary.main, fontWeight: theme.typography.fontWeightBold },
  '&.is-drag-over': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    outline: `1px dashed ${theme.palette.primary.main}`,
    outlineOffset: -1,
  },
  // styled() drops Tab's overridable generic — restore it so `component`/`href` stay typed.
})) as typeof Tab;

// DA-19731: holds the bar's box while the block is still being fetched, so the sticky subheader
// keeps its height and nothing below it jumps. Reuses barMetrics, so it cannot drift from the real
// bar. Needed only on a cold grid load: when the tile Redux block is warm it is handed to the query
// as placeholderData and the real tabs render immediately.
const SkeletonRow = styled(Stack, { label: 'WorkspaceDoctypeTabsSkeleton' })(({ theme }) => ({
  ...barMetrics(theme),
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(3),
}));

// `role="status"` + `aria-busy`, not `aria-hidden`: the bar IS loading, and assistive tech should
// hear that rather than find a hole where the bar will appear.
const DoctypeTabsSkeleton = () => (
  <SkeletonRow role="status" aria-busy="true">
    <CategorizationSkeletons count={3} width={120} height={20} />
  </SkeletonRow>
);

// Financial/General tabs are reclassify drop targets (spec 4.1). `useDrop` on the Tab ref keeps
// it a direct <Tabs> child (indicator/value intact). The All tab is not a target. Always a bulk
// write over the current selection (or the single dragged doc when nothing is selected).
const TAB_WRITE: Record<string, { type: string; toastId: string; toastMsg: string }> = {
  financial: { type: CONST.tags.financial, toastId: 'document.actions.financial.success', toastMsg: 'Document marked as financial' },
  general: { type: CONST.tags.general, toastId: 'document.actions.general.success', toastMsg: 'Document marked as general' },
};

// `Tabs` injects `selected`/`textColor`/`indicator`/`onChange`/`tabIndex`/`fullWidth` into every child
// via cloneElement (MUI Tabs.js) — this wrapper MUST forward them untouched. Swallowing `textColor`
// drops Tab to its own `'inherit'` default (opacity 0.6, lifted to 1 only by `.Mui-selected`), and
// swallowing `selected` means the tab never reaches that state at all. `TabProps` is parameterised on
// RouterLink so the spread's DOM handlers are anchor-typed and fit MUI's `href` overload; `component`
// and `href` are omitted because they are set literally below.
type DroppableTabProps = Omit<TabProps<typeof RouterLink>, 'ref' | 'component' | 'href'> & {
  href: string;
  writeType: string;
  toastId: string;
  toastMsg: string;
  selectedIds: string[];
  clearSelection: () => void;
};

const DroppableTab = ({
  href,
  writeType,
  toastId,
  toastMsg,
  selectedIds,
  clearSelection,
  ...tabProps
}: DroppableTabProps) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLAnchorElement>(null);
  const canReclassify = useCanReclassify(writeType);
  // The drag item comes from the legacy DnDItemSource (@/components/DnDItem); its runtime shape is
  // `{ id, ...params, itemType }` and we read `id`. `type` is not on the item — it is here only
  // because react-dnd v7's useDrop generic must extend DragObjectWithType ({ type }).
  const [{ isOver, canDrop }, drop] = useDrop<{ type: string; id: string }, unknown, { isOver: boolean; canDrop: boolean }>({
    accept: DOCUMENT,
    // DA-19719: the tab the document already sits on is not a drop target. Same id set as the drop
    // below, so the guard and the write can't disagree.
    canDrop: (item) => Boolean(item?.id) && canReclassify(selectedIds.length ? selectedIds : [item.id]),
    drop: (item) => {
      if (!item?.id) return;
      const documentsIds = selectedIds.length ? selectedIds : [item.id];
      new Promise((resolve, reject) => {
        dispatch({ type: documentsChangeTypesAction.type, payload: { documentsIds, type: writeType, resolve, reject } });
      })
        // DA-19760: a successful drop ends the bulk action, so it clears the selection and with it the
        // bulk panel — parity with every other bulk action (CompanyBulkPanel.changeTypes, useDotsMenu).
        // Only on resolve: a failed write keeps the selection so the user can retry.
        .then(() => {
          toast.info(formatMessage({ id: toastId, defaultMessage: toastMsg }));
          clearSelection();
        })
        .catch(() => null);
    },
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  });
  drop(ref);
  // `draggable={false}`: the tab is a drop TARGET, and a native anchor is implicitly draggable —
  // grabbing it would start a browser link-drag instead of leaving it a passive target.
  return (
    <StyledTab
      ref={ref}
      component={RouterLink}
      href={href}
      draggable={false}
      {...tabProps}
      className={cx({ 'is-drag-over': isOver && canDrop })}
    />
  );
};

const WorkspaceDoctypeTabs = ({
  selectedIds = [],
  clearSelection,
}: {
  selectedIds?: string[];
  clearSelection: () => void;
}) => {
  const allowed = useSelector(documentCategorizationAllowedSelector);
  const doctypeLookup = useSelector(doctypeTabLookupSelector);
  const categoriesList = useSelector(categoriesListSelector);
  const { doctypes, isLoading } = useWorkspaceCategorization();
  const { companyId, category1, category2, category3 } = useParams();
  const location = useLocation();
  const { companyType } = useContext(CompanyWorkspaceContext);

  if (!allowed) return null;
  // To-Approve (and any non-eligible category) is suppressed by the backend returning no tabs.
  // DA-19731: "no tabs" and "not fetched yet" are different states — an empty block while the
  // request is in flight holds its box with a skeleton instead of collapsing the subheader.
  if (doctypes.length === 0) return isLoading ? <DoctypeTabsSkeleton /> : null;

  // Active tab is derived from the stable all-categories catalog (loaded with the sidebar), NOT the
  // per-category aggregations, which lag one navigation behind: on navigation `deepest` flips to the
  // destination's tab id instantly while `doctypes` still holds the previous category's tabs, so a
  // `doctypes`-based match briefly falls back to All and the indicator animates All→Financial. The
  // catalog resolves the deepest segment's name_id immediately, so the active tab is correct at once.
  const deepest = getNestedCategory({ category1, category2, category3 });
  const value = doctypeLookup.get(String(deepest)) ?? 'all';

  const template =
    companyType === 'confidential'
      ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute
      : ROUTES_PATH.COMPANY_WORKSPACE.absolute;

  // Strip the deepest slot when it is a doctype tab (Fin/Gen), and resolve each tab's routing id —
  // both from the stable catalog (URL lookup + the current category's `tabs`), NOT the aggregations
  // `doctypes`, which lag one navigation behind and would yield the previous category's per-tab ids.
  const filled = pipe(
    [category1, category2, category3],
    A.filter((s): s is string => Boolean(s)),
  );
  const onDoctypeSegment = value === CONST.doctype.financial || value === CONST.doctype.general;
  const base = onDoctypeSegment ? filled.slice(0, -1) : filled;
  const currentCat = findCatById(categoriesList, base[base.length - 1]);

  // DA-19705: `location.search` carries the global query (search chips, date ranges, plus keys this
  // component doesn't own) into every tab link. The query lives ONLY in the URL (`querySelector` =
  // `location.query`, re-derived from `search` on every LOCATION_CHANGE), and per spec D9 the
  // doctype tab is a level-2 filter that must not touch the level-1 global filter. Appended
  // verbatim, never re-serialized.
  const hrefFor = (nextName: string) => {
    const nextId = currentCat?.tabs?.find((t: { name_id: string; id: string | number }) => t.name_id === nextName)?.id;
    const [nextCat1, nextCat2, nextCat3] = nextName === 'all' || nextId == null ? base : [...base, String(nextId)];

    return `${generatePath(template, {
      companyId,
      category1: nextCat1,
      category2: nextCat2,
      category3: nextCat3,
    })}${location.search}`;
  };

  return (
    <StyledTabs value={value}>
      {doctypes.map(({ name_id, label, count }) =>
        TAB_WRITE[name_id] ? (
          <DroppableTab
            key={name_id}
            value={name_id}
            label={`${label} (${count})`}
            href={hrefFor(name_id)}
            selectedIds={selectedIds}
            clearSelection={clearSelection}
            writeType={TAB_WRITE[name_id].type}
            toastId={TAB_WRITE[name_id].toastId}
            toastMsg={TAB_WRITE[name_id].toastMsg}
          />
        ) : (
          <StyledTab
            key={name_id}
            value={name_id}
            label={`${label} (${count})`}
            component={RouterLink}
            href={hrefFor(name_id)}
          />
        ),
      )}
    </StyledTabs>
  );
};

export default WorkspaceDoctypeTabs;
